import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import { LooseValue } from 'react-calendar/dist/cjs/shared/types';
import { ChatBotComponentProps } from '.';
import { useTranslation } from '../../tools/i18n';
import { isNightCustomer } from '../../tools/utils';
import { Picto } from '../Picto';
import { DayNumberSolo } from './DayNumber';
import { FilterLayout } from './FiltersLayout';
import { TravelPlannerPeriodsOrDatesType } from './types';

export interface PeriodsOrDatesProps extends ChatBotComponentProps {
  step: TravelPlannerPeriodsOrDatesType;
}

export enum Months {
  january = 'Janvier',
  february = 'Février',
  march = 'Mars',
  april = 'Avril',
  may = 'Mai',
  june = 'Juin',
  july = 'Juillet',
  august = 'Août',
  september = 'Septembre',
  october = 'Octobre',
  november = 'Novembre',
  december = 'Décembre',
}

const getMaxTripLength = (step: TravelPlannerPeriodsOrDatesType, stateData: any) =>
  stateData?.selectedFavorite ? (step.maxDaysFavorite ?? 7) : (step.maxDays ?? 5);

const PeriodsOrDatesCalendarOnly = (props: PeriodsOrDatesProps) => {
  const { config, step, callback, stateData, back, requirements } = props;
  const { t, language } = useTranslation();

  const isNight = isNightCustomer(requirements?.config?.key);
  const maxTripLength = getMaxTripLength(step, stateData);
  const maxSpan = Math.max(1, maxTripLength - 1);
  const spanToTripLength = (spanInDays: number) => (isNight ? spanInDays : spanInDays + 1);

  const [range, setRange] = useState<Date[] | null>(null);
  const [activeStartDate, setActiveStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 479px)');
    const handleChange = () => setIsMobile(mql.matches);
    handleChange();
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  const isComplete = range?.length === 2;

  const handleChange = (value: any) => {
    if (Array.isArray(value)) {
      setRange(value.filter(Boolean) as Date[]);
    } else if (value instanceof Date) {
      setRange([value]);
    } else {
      setRange(null);
    }
  };

  const handleSubmit = () => {
    if (!isComplete || !range) return;
    const [start, end] = range;
    const span = Math.round((end.getTime() - start.getTime()) / 86400000);
    const tripLength = spanToTripLength(span);
    callback({
      tripLength,
      startDate: start,
      month: undefined,
    });
  };

  const tileDisabled = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return false;
    if (range?.length !== 1) return false;
    const start = range[0];
    const maxEnd = new Date(start);
    maxEnd.setDate(maxEnd.getDate() + maxSpan);
    const minStart = new Date(start);
    minStart.setDate(minStart.getDate() - maxSpan);
    return date > maxEnd || date < minStart;
  };

  const locale = language?.split(/[-_]/)?.[0];
  const monthYearFormatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  });

  const renderNavigationLabel = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') {
      return monthYearFormatter.format(date);
    }
    return monthYearFormatter.formatToParts(date).map((p, i) => (
      <span key={`${p.type}-${i}`} className={`monthLabel__${p.type}`}>
        {p.value}
      </span>
    ));
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month' || !range || range.length === 0) return null;
    const start = range[0];
    const span = Math.round((date.getTime() - start.getTime()) / 86400000);
    if (span < 1) return null;
    return (
      <span className="rangeTooltip">
        {t('travelPlaner.PeriodsOrDates.days', { count: spanToTripLength(span) })}
      </span>
    );
  };

  return (
    <div className="periodsOrDates periodsOrDates--calendarOnly">
      <FilterLayout
        config={config}
        callback={handleSubmit}
        title={t('travelPlaner.PeriodsOrDates.title')}
        back={back}
        requirements={requirements}
        disabledNext={!isComplete}
        siteConfiguration={props.siteConfiguration}
      >
        <Calendar
          selectRange
          allowPartialRange
          showDoubleView={!isMobile}
          showNeighboringMonth={false}
          showFixedNumberOfWeeks={false}
          goToRangeStartOnSelect={false}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={({ action, activeStartDate: next }) => {
            if (action !== 'onChange' && next) setActiveStartDate(next);
          }}
          value={range as LooseValue}
          onChange={handleChange}
          minDate={new Date()}
          tileDisabled={tileDisabled}
          prev2Label={null}
          next2Label={null}
          prevLabel={<Picto iconKey="caret-left" />}
          nextLabel={<Picto iconKey="caret-right" />}
          navigationLabel={renderNavigationLabel}
          tileContent={tileContent}
          locale={locale}
          tileClassName="periodsOrDates calendarTile"
        />
        <div className="periodsOrDates__footer">
          {t('travelPlaner.PeriodsOrDates.maxLength', { count: spanToTripLength(maxSpan) })}
        </div>
      </FilterLayout>
    </div>
  );
};

const PeriodsOrDatesCalendarAndLength = (props: PeriodsOrDatesProps) => {
  const { config, step, callback, stateData, back, requirements } = props;
  const [tabs, setTabs] = useState(step?.preciseDatesOnly ? 2 : 1);
  const [tripLength, setTripLength] = useState(stateData?.searchMap?.length || 1);
  const [month, setMonth] = useState('');
  const [startDate, setStartDate] = useState<LooseValue>(new Date());
  const { t, language } = useTranslation();

  const maxTripLength = getMaxTripLength(step, stateData);

  const handleChangePeriodsOrDates = () => {
    const data = {
      tripLength,
      month: tabs === 1 ? month : undefined,
      startDate: tabs === 2 ? startDate : undefined,
    };

    callback(data);
  };

  return (
    <div>
      <FilterLayout
        config={config}
        callback={handleChangePeriodsOrDates}
        title={t('travelPlaner.PeriodsOrDates.title')}
        back={back}
        requirements={requirements}
        disabledNext={(!month && tabs === 1) || (!startDate && tabs === 2)}
        siteConfiguration={props.siteConfiguration}
      >
        {step?.preciseDatesOnly ? null : (
          <div className="periodsOrDates__container">
            <div className="periodsOrDates__header">
              <div
                onClick={() => (tabs === 1 ? undefined : setTabs(1))}
                className={
                  tabs === 1
                    ? 'periodsOrDates__tabs periodsOrDates__tabs__full'
                    : 'periodsOrDates__tabs'
                }
              >
                <div className="periodsOrDates__tabs__pictoText">
                  <Picto
                    fill={tabs === 1 ? '#1836C5' : '#6D6D6D'}
                    className="periodsOrDate__tabs__picto"
                    iconKey={'periods'}
                  />
                  <div className="periodsOrDates__tabs__text">
                    {t('travelPlaner.PeriodsOrDates.periods')}
                  </div>
                </div>
                <div
                  className={
                    tabs === 1
                      ? 'peridosOrDates__tabs__underline__full peridosOrDates__tabs__underline'
                      : 'peridosOrDates__tabs__underline'
                  }
                />
              </div>
              <div
                onClick={() => (tabs === 2 ? undefined : setTabs(2))}
                className={
                  tabs === 2
                    ? 'periodsOrDates__tabs periodsOrDates__tabs__full'
                    : 'periodsOrDates__tabs'
                }
              >
                <div className="periodsOrDates__tabs__pictoText">
                  <Picto
                    className="periodsOrDate__tabs__picto"
                    fill={tabs === 2 ? '#1836C5' : '#6D6D6D'}
                    iconKey={'dates'}
                  />
                  <div className="periodsOrDates__tabs__text">
                    {t('travelPlaner.PeriodsOrDates.dates')}
                  </div>
                </div>
                <div
                  className={
                    tabs === 2
                      ? 'peridosOrDates__tabs__underline__full peridosOrDates__tabs__underline'
                      : 'peridosOrDates__tabs__underline'
                  }
                />
              </div>
            </div>
          </div>
        )}
        {tabs === 1 ? (
          <>
            <div className="periodsMonthContainer">
              {Object.keys(Months).map((m) => (
                <div
                  key={m}
                  className={month === m ? 'selectedMonth' : 'periodsMonth'}
                  onClick={() => setMonth(m)}
                >
                  <Picto className="periodsMonthPicto" fill="#1836C5" iconKey={'dates'} />
                  <div className="periodsMonthText">
                    {t(`travelPlaner.PeriodsOrDates.months.${m}`)}
                  </div>
                </div>
              ))}
            </div>
            <div className="periodsDay">
              <div className="periodsDayText">{t('travelPlaner.PeriodsOrDates.duration')}</div>
              <DayNumberSolo
                number={tripLength}
                setNumber={setTripLength}
                maxLength={maxTripLength}
                minLength={stateData.searchMap.length}
              />
            </div>
          </>
        ) : undefined}
        {tabs === 2 ? (
          <>
            <Calendar
              value={startDate}
              onChange={setStartDate}
              minDate={new Date()}
              defaultView={'month'}
              prev2Label={requirements?.config?.newUI ? null : '«'}
              next2Label={requirements?.config?.newUI ? null : '»'}
              prevLabel={<Picto iconKey="circle-play" className="prev" />}
              nextLabel={<Picto iconKey="circle-play" />}
              locale={language?.split(/[-_]/)?.[0]}
              tileClassName="periodsOrDates calendarTile"
            />
            <div className="periodsDay">
              <div className="periodsDayText">{t('travelPlaner.PeriodsOrDates.duration')}</div>
              <DayNumberSolo
                number={tripLength}
                setNumber={setTripLength}
                maxLength={maxTripLength}
                minLength={stateData.searchMap.length}
              />
            </div>
          </>
        ) : undefined}
      </FilterLayout>
    </div>
  );
};

export const PeriodsOrDates = (props: PeriodsOrDatesProps) => {
  const version = props.step?.version ?? 'calendarAndLength';

  if (version === 'calendarOnly') {
    return <PeriodsOrDatesCalendarOnly {...props} />;
  }

  return <PeriodsOrDatesCalendarAndLength {...props} />;
};
